import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVideos, getCreditStatus } from '../services/api';
import ModernVideoCard from '../components/ModernVideoCard';
import RightSideMenu from '../components/RightSideMenu';
import SubscriptionModal from '../components/SubscriptionModal';
import UnifiedHeader from '../components/UnifiedHeader';
import { authService } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './AllVideosPage.css';

const VIDEOS_PER_PAGE = 12;

export default function AllVideosPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const creditStatusRef = useRef(null);
  
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  
  // Right menu and subscription
  const [showRightMenu, setShowRightMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [creditStatus, setCreditStatus] = useState(null);
  
  const user = authService.getUser();
  const totalPages = Math.ceil(totalVideos / VIDEOS_PER_PAGE);

  useEffect(() => {
    loadCreditStatus();
  }, []);

  const loadCreditStatus = async () => {
    try {
      const status = await getCreditStatus();
      setCreditStatus(status);
    } catch (err) {
      console.error('Error loading credit status:', err);
    }
  };

  const handleOpenSubscription = () => {
    setShowSubscriptionModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCloseSubscription = () => {
    setShowSubscriptionModal(false);
    document.body.classList.remove('modal-open');
  };

  const handleSelectPlan = (plan) => {
    console.log('Selected plan:', plan);
  };

  useEffect(() => {
    const fetchVideos = async (page = 1) => {
      try {
        setLoading(true);
        const offset = (page - 1) * VIDEOS_PER_PAGE;
        const data = await getVideos(VIDEOS_PER_PAGE, offset);
        setVideos(data.videos);
        setTotalVideos(data.total || data.videos.length);
        setError(null);
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError(translations[language].failedToLoad);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos(currentPage);
  }, [currentPage, language]);

  const handleDelete = async (videoId) => {
    //await deleteVideo(videoId);
    setVideos(videos.filter(v => v.id !== videoId));
    setTotalVideos(prev => prev - 1);
    
    // Refresh credit status after deletion
    if (creditStatusRef.current) {
      creditStatusRef.current.refresh();
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };  

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setCurrentPage(prev => prev);
  };

  return (
    <div className="all-videos-page">
      {/* Unified Header */}
      <UnifiedHeader
        creditStatusRef={creditStatusRef}
        onOpenSubscription={handleOpenSubscription}
        onOpenMenu={() => setShowRightMenu(true)}
        showCreditStatus={true}
      />

      {/* Main Content */}
      <main className="page-main">
        <div className="page-content">
          {/* Page Header */}
          <div className="content-header">            
            <div className="header-info">
              <h1>{t.allVideos}</h1>
              <p className="video-count">{totalVideos} {t.totalVideos}</p>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{t.loadingVideos}</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={handleRetry} className="btn-retry">
                {t.retry}
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && videos.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🎬</div>
              <h3>{t.noVideosYet}</h3>
              <p>{t.noVideosDesc}</p>
              <button onClick={() => navigate('/dashboard')} className="btn-create">
                {t.createVideo}
              </button>
            </div>
          )}

          {/* Videos Grid */}
          {!loading && !error && videos.length > 0 && (
            <>
              <div className="videos-grid">
                {videos.map((video) => (
                  <ModernVideoCard
                    key={video.id}
                    video={video}
                    onDelete={handleDelete}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="page-btn prev-btn"
                  >
                    <ChevronLeft size={20} />
                    <span>{t.previous}</span>
                  </button>

                  <div className="page-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`page-number ${page === currentPage ? 'active' : ''}`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="page-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="page-btn next-btn"
                  >
                    <span>{t.next}</span>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Right Side Menu */}
      <RightSideMenu
        isOpen={showRightMenu}
        onClose={() => setShowRightMenu(false)}
        user={user}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onOpenSubscription={handleOpenSubscription}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseSubscription}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
}
